import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  reopenReviewForTerminologyItem,
  updateTerminologyItem,
} from "@/api/terminology";
import type { TerminologyItem } from "@/api/terminology";
import { useToast } from "@/components/toast";

const TRUSTED_SOURCES = new Set([
  "human",
  "regulatory_aligned",
  "expert_reviewed",
]);

type UsageMode =
  | "hard"
  | "hint"
  | "highlight_only"
  | "candidate"
  | "forbidden";

function computeMode(item: TerminologyItem): UsageMode {
  const status = item.status ?? "candidate";
  if (status === "forbidden") return "forbidden";
  if (status !== "approved") return "candidate";
  const ext = item.external_ids ?? {};
  const ce = (ext as Record<string, unknown>).constraint_eligible;
  const ruSrc = String((ext as Record<string, unknown>).ru_term_source ?? "");
  const morphSafe = readMorphSafe(ext as Record<string, unknown>);
  const isHardEligible = ce === true || ce === "true";
  const isTrusted = TRUSTED_SOURCES.has(ruSrc);
  // hard режим требует ce + morph_safe + trusted source. morph_safe
  // default=true (backward-compat для существующих ~20k записей без
  // явного флага). См. trust.resolve_glossary_morph_safe.
  if (isHardEligible && isTrusted && morphSafe) return "hard";
  if (item.item_type === "term_alias") return "highlight_only";
  return "hint";
}

function readMorphSafe(ext: Record<string, unknown>): boolean {
  const raw = ext.morph_safe;
  if (raw === false || raw === "false" || raw === 0) return false;
  return true;
}

const MODE_BADGES: Record<UsageMode, { label: string; cls: string; help: string }> = {
  hard: {
    label: "Hard enforcement",
    cls: "mt-badge--success",
    help:
      "При переводе делается жёсткая замена RU-альтернатив на canonical из этой записи (constraint_eligible=true + trusted ru_term_source).",
  },
  hint: {
    label: "Hint only",
    cls: "mt-badge--info",
    help:
      "Запись попадает в LLM-context как подсказка переводчику, но автозамены не происходит (constraint_eligible=false или machine_candidate).",
  },
  highlight_only: {
    label: "Только подсветка",
    cls: "mt-badge--info",
    help:
      "term_alias-запись помогает matcher'у находить вариации в EN-тексте; в перевод напрямую не вмешивается.",
  },
  candidate: {
    label: "Не активна",
    cls: "mt-badge--neutral",
    help:
      "status=candidate — запись лежит в БД, но resolver её игнорирует. Нужно одобрить чтобы попала в перевод.",
  },
  forbidden: {
    label: "Запрещена",
    cls: "mt-badge--danger",
    help:
      "status=forbidden — переводчик получит warning при попытке использовать эту форму.",
  },
};

export function TerminologyUsageBlock({
  item,
  reviewer,
}: {
  item: TerminologyItem;
  reviewer: string;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const mode = useMemo(() => computeMode(item), [item]);
  const badge = MODE_BADGES[mode];
  const morphSafe = readMorphSafe(
    (item.external_ids ?? {}) as Record<string, unknown>,
  );

  const isHardable = item.item_type !== "term_alias";

  const mutation = useMutation({
    mutationFn: async (patch: {
      label: string;
      status?: string;
      externalIdsPatch?: Record<string, unknown>;
    }) => {
      const ext = (item.external_ids ?? {}) as Record<string, unknown>;
      const merged = {
        ...ext,
        ...(patch.externalIdsPatch ?? {}),
      };
      const r = await updateTerminologyItem(item.item_id, {
        reviewer,
        ...(patch.status ? { status: patch.status } : {}),
        external_ids: merged,
      });
      if (!r.ok) throw new Error(r.error);
      return { ...r.data, _label: patch.label };
    },
    onSuccess: (data) => {
      toast(`${(data as { _label: string })._label} применено`, "success");
      // Мгновенно кладём свежий item из ответа в кэш карточки, чтобы бейдж
      // режима (hard/hint) пересчитался сразу. Без этого он менялся только
      // после ручной перезагрузки страницы.
      const updated = (data as { item?: unknown }).item;
      if (updated) {
        qc.setQueryData(
          ["terminology-item", item.item_id],
          (old: unknown) =>
            old && typeof old === "object"
              ? { ...(old as Record<string, unknown>), item: updated }
              : old,
        );
      }
      // Инвалидируем по префиксу (без id): активная карточка может жить под
      // ключом с URL-itemId, не обязательно равным item.item_id.
      void qc.invalidateQueries({ queryKey: ["terminology-item"] });
      void qc.invalidateQueries({ queryKey: ["terminology-items"] });
    },
    onError: (err: Error) =>
      toast(`Не применено: ${err.message}`, "error"),
  });

  function approveAsHint() {
    mutation.mutate({
      label: "Одобрено как hint",
      status: "approved",
      externalIdsPatch: {
        constraint_eligible: false,
        ru_term_source: "expert_reviewed",
      },
    });
  }
  function approveAsHard() {
    mutation.mutate({
      label: "Одобрено как hard",
      status: "approved",
      externalIdsPatch: {
        constraint_eligible: true,
        ru_term_source: "expert_reviewed",
        morph_safe: true,
      },
    });
  }
  function promoteToHard() {
    mutation.mutate({
      label: "Promote to hard",
      externalIdsPatch: {
        constraint_eligible: true,
        ru_term_source: "expert_reviewed",
        morph_safe: true,
      },
    });
  }
  function toggleMorphSafe(next: boolean) {
    mutation.mutate({
      label: next ? "morph_safe = true" : "morph_safe = false",
      externalIdsPatch: { morph_safe: next },
    });
  }

  const reopenMutation = useMutation({
    mutationFn: async () => {
      const r = await reopenReviewForTerminologyItem(item.item_id, {});
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: (data) => {
      toast(
        `Открыто в ревью (stable_key: ${data.stable_key.slice(0, 32)}…)`,
        "success",
      );
      navigate(
        `/admin/review/focus/${encodeURIComponent(data.review_item_id)}`,
      );
    },
    onError: (err: Error) => toast(`Не удалось: ${err.message}`, "error"),
  });
  function demoteToHint() {
    mutation.mutate({
      label: "Demote to hint",
      externalIdsPatch: { constraint_eligible: false },
    });
  }
  function forbid() {
    mutation.mutate({
      label: "Forbidden",
      status: "forbidden",
    });
  }

  const disabled = mutation.isPending;

  return (
    <section className="rf-section">
      <div className="rf-section__head">
        <span>Использование при переводе</span>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <span
          className={`mt-badge mt-badge--no-dot ${badge.cls}`}
          title={badge.help}
        >
          {badge.label}
        </span>
      </div>
      <div className="rf-section__body">
        <div
          className="mt-text-tight mt-muted"
          style={{ marginBottom: 10, textTransform: "none", letterSpacing: 0 }}
        >
          {badge.help}
        </div>
        <div
          data-tour="usage-actions"
          style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
        >
          {mode === "candidate" && (
            <>
              <button
                className="mt-btn mt-btn--secondary mt-btn--sm"
                disabled={disabled}
                onClick={approveAsHint}
                type="button"
                title="Одобрить как подсказку — без автозамены в RU-тексте"
              >
                Одобрить как hint
              </button>
              {isHardable && (
                <button
                  className="mt-btn mt-btn--primary mt-btn--sm"
                  disabled={disabled}
                  onClick={approveAsHard}
                  type="button"
                  title="Одобрить + включить hard enforcement (автозамену)"
                >
                  Одобрить как hard
                </button>
              )}
            </>
          )}
          {mode === "hint" && isHardable && (
            <button
              className="mt-btn mt-btn--primary mt-btn--sm"
              disabled={disabled}
              onClick={promoteToHard}
              type="button"
              title="Включить hard enforcement"
            >
              Promote to hard
            </button>
          )}
          {mode === "hard" && (
            <button
              className="mt-btn mt-btn--secondary mt-btn--sm"
              disabled={disabled}
              onClick={demoteToHint}
              type="button"
              title="Оставить только как подсказку, без автозамены"
            >
              Demote to hint
            </button>
          )}
          {(mode === "candidate" ||
            mode === "hint" ||
            mode === "hard" ||
            mode === "highlight_only") && (
            <button
              className="mt-btn mt-btn--danger mt-btn--sm"
              disabled={disabled}
              onClick={forbid}
              type="button"
              title="Запретить использование — warning переводчику"
            >
              Forbid
            </button>
          )}
          {mode === "forbidden" && (
            <button
              className="mt-btn mt-btn--secondary mt-btn--sm"
              disabled={disabled}
              onClick={approveAsHint}
              type="button"
              title="Снять запрет, одобрить как hint"
            >
              Восстановить как hint
            </button>
          )}
          {mutation.isPending && (
            <span
              className="mt-text-tight mt-muted"
              style={{ alignSelf: "center" }}
            >
              применяем…
            </span>
          )}
          <span style={{ flex: 1 }} />
          {(mode === "hard" || mode === "hint" || mode === "highlight_only") && (
            <button
              className="mt-btn mt-btn--ghost mt-btn--sm"
              disabled={disabled || reopenMutation.isPending}
              onClick={() => reopenMutation.mutate()}
              type="button"
              title="Создать новый review-item для этой записи и редиректнуть в /admin/review/focus"
            >
              {reopenMutation.isPending ? "Открываем…" : "↻ Открыть в ревью"}
            </button>
          )}
        </div>

        {isHardable && (mode === "hard" || mode === "hint") && (
          <label
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              fontSize: "var(--fs-sm)",
            }}
            title={
              "Когда выключено — даже при constraint_eligible=true hard-замена" +
              " не применяется. Используйте для терминов, где грамматика" +
              " замены зависит от контекста (склонения, род, число)."
            }
          >
            <input
              type="checkbox"
              checked={morphSafe}
              onChange={(e) => toggleMorphSafe(e.target.checked)}
              disabled={disabled}
              style={{ marginTop: 3 }}
            />
            <span>
              <strong>Безопасна для прямой замены без контекста?</strong>{" "}
              <span className="mt-muted">
                (морфология / число / падеж canonical'а подходят как есть в
                любом окружении)
              </span>
            </span>
          </label>
        )}
      </div>
    </section>
  );
}
